import { useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChatButton from "@/components/FloatingChatButton";
import WhatsAppWidget from "@/components/WhatsAppWidget";
import SeoHead from "@/components/seo/SeoHead";
import { SeoHero, SeoSection, InfoCards, FaqList, LinkChips, CtaBand } from "@/components/seo/SeoBlocks";
import { useLanguage } from "@/contexts/LanguageContext";
import { LABELS } from "@/seo/seoLabels";
import { cityPages } from "@/seo/cityPages";
import { servicePages } from "@/seo/servicePages";

const ServicePage = ({ page }) => {
  const { language: lang } = useLanguage();
  const L = LABELS[lang];
  useEffect(() => { window.scrollTo(0, 0); }, [page.slug]);

  const related = servicePages.filter((s) => page.related.includes(s.slug));

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`service-page-${page.slug}`}>
      <SeoHead title={page.title} description={page.metaDescription} path={`/${page.slug}`} />
      <Navigation />
      <SeoHero
        crumbs={[{ label: L.home, to: "/" }, { label: L.services, to: "/#servicios" }, { label: page.name[lang] }]}
        eyebrow={L.serviceEyebrow}
        h1={page.h1[lang]}
        intro={page.intro[lang][0]}
      />

      <SeoSection testId="service-intro">
        <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-4xl">{page.intro[lang][1]}</p>
      </SeoSection>

      <SeoSection alt title={L.commonProblems} testId="service-problems">
        <InfoCards items={page.commonProblems[lang]} />
      </SeoSection>

      <SeoSection title={L.whatInvolves} testId="service-process">
        <InfoCards items={page.process[lang]} numbered />
      </SeoSection>

      <SeoSection alt title={L.signs} testId="service-signs">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {page.signs[lang].map((s, i) => (
            <li key={i} className="flex items-start gap-2 bg-white border border-slate-200 p-3 md:p-4 text-sm md:text-base text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />{s}
            </li>
          ))}
        </ul>
      </SeoSection>

      <SeoSection title={L.whyProfessional} testId="service-why">
        <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-4xl">{page.whyProfessional[lang]}</p>
      </SeoSection>

      <SeoSection alt title={L.areasServed} testId="service-areas">
        <p className="text-slate-600 text-sm md:text-base mb-5 max-w-4xl">{L.areasServedText}</p>
        <LinkChips items={[...cityPages.map((c) => ({ label: `${c.city}, VA`, to: `/${c.slug}` })), { label: L.allOfHamptonRoads }]} testId="service-city-links" />
      </SeoSection>

      <SeoSection title={L.faq} testId="service-faq">
        <FaqList items={page.faq[lang]} />
      </SeoSection>

      <SeoSection alt title={L.relatedServices} testId="service-related">
        <LinkChips items={related.map((s) => ({ label: s.name[lang], to: `/${s.slug}` }))} testId="related-service-links" />
      </SeoSection>

      <CtaBand title={L.ctaBandService} text={L.ctaBandText} />
      <Footer />
      <FloatingChatButton />
      <WhatsAppWidget />
    </div>
  );
};

export default ServicePage;
